import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { FC } from 'react';
import { useNavigate } from 'react-router';
import { ownerContributorAndViewerIds } from 'src/components/form';
import { useI18NSummaryHelpContent } from 'src/components/help-panel/useSummaryHelpContent';

import { useMultiParentFileUpdate } from '@/hooks/mutations/file/useFileUpdate';
import { useInsertThirdParty } from '@/hooks/mutations/third-party';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useIsFormPrefillEnabled } from '@/hooks/useIsFormPrefillEnabled';
import { createWithFiles } from '@/utils/createWithFiles';
import { thirdPartyDetailsUrl } from '@/utils/urls';

import { ThirdPartyForm } from '../../forms/ThirdPartyForm';
import type { ThirdPartyFormData } from '../../forms/thirdPartySchema';

const ThirdPartyCreateTab: FC = () => {
  useI18NSummaryHelpContent('third_party.help');
  const navigate = useNavigate();
  const { insertThirdParty } = useInsertThirdParty();
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  const formPrefillEnabled = useIsFormPrefillEnabled();
  const { updateFiles } = useMultiParentFileUpdate();

  const onSave = async (data: ThirdPartyFormData) => {
    const { files } = data;
    const { createdEntity } = await createWithFiles({
      create: () =>
        insertThirdParty({
          Title: data.title,
          Description: data.description,
          CompanyName: data.companyName,
          CompaniesHouseNumber: data.companiesHouseNumber,
          Address: data.address,
          CityTown: data.cityTown,
          Postcode: data.postcode,
          Country: data.country,
          PrimaryContactName: data.primaryContactName,
          ContactName: data.contactName,
          ContactEmail: data.contactEmail,
          CompanyDomain: data.companyDomain,
          Type: data.type,
          Status: data.status,
          Criticality: data.criticality,
          CustomAttributeData: data.CustomAttributeData,
          TagTypeIds: data.tags?.map((t) => t.TagTypeId) || [],
          DepartmentTypeIds:
            data.departments?.map((d) => d.DepartmentTypeId) || [],
          ...ownerContributorAndViewerIds(data),
        }),
      getCreatedEntity: (result) => {
        const thirdPartyId = result?.insertThirdPartyApi?.Id;
        if (!thirdPartyId) {
          throw new Error('Third-party id is missing');
        }

        return {
          id: thirdPartyId,
          destination: thirdPartyDetailsUrl(thirdPartyId),
        };
      },
      saveFiles: ({ id }) =>
        updateFiles({
          parentType: Parent_Type_Enum.ThirdParty,
          parentIds: [id],
          originalFiles: [],
          selectedFiles: files,
        }),
    });

    return {
      savedIds: [createdEntity.id],
      createdEntity,
      successNavigation: {
        destination: createdEntity.destination,
        isReplace: true,
      },
    };
  };

  const onDismiss = (saved?: boolean) => {
    if (!saved) {
      navigate(-1);
    }
  };

  return (
    <ThirdPartyForm
      onSave={onSave}
      onDismiss={onDismiss}
      publishToolbarState={objectToolbarEnabled}
      publishActiveForm={formPrefillEnabled}
    />
  );
};

export default ThirdPartyCreateTab;
